<template>
  <div class="datepicker-time align-items-center justify-content-between flex-wrap pt-3 d-flex position-relative w-100">
    <div class="d-flex flex-nowrap w-100 justify-content-between">
      <div class="d-flex flex-nowrap align-items-center">
        <sdui-icon class="icon-clock-o-16 icon dark" />
        <div class="position-relative">
          <SInput
            v-cloak
            id="start-at-id"
            ref="timeInput"
            v-model="inputValue"
            validation-field-name="time"
            validation-rules="required"
            :label-text="$t('ui.date_time_picker.time')"
            hide-label
            type="time"
            test-id="time-picker"
            class="datepicker-time__input ml-2 pseudo-input"
            data-cy="time-picker-input"
            min="00:00"
            max="23:59"
            :aria-label="$t('ui.date_time_picker.time')"
            @focus="handleFocus"
            @blur="handleBlur"
            @change="handleChange"
          />
          <span v-if="beginOrEnd && toggleLabel" class="datepicker-time__label text-break">{{ labelText }}</span>
        </div>
      </div>

      <b-button
        variant="transparent"
        class="d-flex align-items-center btn-link pl-2 reset-button"
        :class="{ 'f-14': hasMobileSize }"
        data-cy="reset-time"
        @click="handleClick"
      >
        <span v-if="ends_at" data-cy="set-time-til-end">{{ $t('ui.date_time_picker.til_end') }}</span>
        <sdui-icon class="icon-arrow-end-o text-primary icon-28 pl-1" :class="{ 'icon-rotate-180': isBeginWithoutEnd }" />
        <span v-if="isBeginWithoutEnd" data-cy="set-time-before-begin">{{ $t('ui.date_time_picker.before_begin') }}</span>
      </b-button>
    </div>
  </div>
</template>

<script>
import dateTimePicker from 'src/mixins/miscellaneous/dateTimePicker.js'
import isMobileDeviceMixing from 'src/mixins/miscellaneous/isMobileDeviceMixin.js'

export default {
  // TODO VUE3: Remove compatConfig when @vue/compat is removed
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },

  name: 'STimePicker',
  mixins: [dateTimePicker, isMobileDeviceMixing],
  props: {
    modelValue: {
      type: String,
      default: '',
    },

    begin_at: {
      type: Boolean,
      default: true,
    },

    ends_at: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    inputValue: '',
    onBlur: false,
    localDate: '',
    toggleLabel: false,
  }),

  computed: {
    beginOrEnd() {
      return this.inputValue === '00:00' || this.inputValue === '23:59'
    },

    isBeginWithoutEnd() {
      return this.begin_at && !this.ends_at
    },

    isAndroid() {
      return this.$store.state.isAndroid
    },

    labelText() {
      if (this.ends_at) return this.$t('ui.date_time_picker.end_of_day')
      if (this.inputValue !== '23:59') return this.$t('ui.date_time_picker.start_of_day')
      return this.$t('ui.date_time_picker.end_of_day')
    },
  },

  emits: ['update:modelValue'],

  methods: {
    dayTime() {
      return this.ends_at ? '23:59' : '00:00'
    },

    // Android WebView sometimes receives focus without opening the native time picker
    // (no keyboard either, since type="time" never shows one). Force it open explicitly.
    handleFocus(event) {
      this.toggleLabel = false
      this.onBlur = true

      if (!this.isAndroid) return

      const inputEl = event.target
      try {
        if (typeof inputEl.showPicker === 'function') {
          inputEl.showPicker()
        } else {
          inputEl.click()
        }
      } catch {
        inputEl.click()
      }
    },

    // Picker commits the value via "change" but on Android WebView the input can stay focused,
    // leaving the custom label hidden. Blur it explicitly so handleBlur restores it.
    // Warning: if the picker gets closed without updating the value (e.g. when selecting the same time
    // or clicking on the cancel button in the native picker), this blur() won't be triggered.
    // The user needs then to blur the field manually. There was no smart way to prevent this.
    handleChange() {
      if (!this.isAndroid) return
      this.$refs.timeInput?.$el?.querySelector('input')?.blur()
    },

    handleBlur() {
      this.toggleLabel = true
      this.onBlur = false
      this.updateTime()
    },

    handleClick() {
      this.inputValue = this.dayTime()
      this.onBlur = true
      this.updateTime()
    },

    updateTime() {
      const date = this.mergeDateTime(this.inputValue, this.localDate, 'time')
      if (this.inputValue.length) {
        this.$emit('update:modelValue', date)
      }
    },
  },

  watch: {
    modelValue: {
      handler(newValue) {
        const { time } = this.getDateTime(newValue)
        this.inputValue = this.$dateFormat.hour_minute(this.$moment(time, 'HH:mm:ss'))
        this.localDate = newValue

        if (this.onBlur) return
        this.toggleLabel = true
      },

      immediate: true,
    },
  },
}
</script>

<style scoped lang="scss">
.datepicker-time {
  border-top: 1px solid $color-gray-100;
}

.datepicker-time__input:focus-within .datepicker-time__label {
  display: none;
}

.datepicker-time__input :deep(input) {
  width: 125px;

  &::-webkit-calendar-picker-indicator {
    display: none;
  }
}

.datepicker-time__label {
  pointer-events: none;
  position: absolute;
  align-items: center;
  background-color: $color-secondary;
  padding-left: 5px;
  height: calc(100% - 2px);
  width: calc(100% - 10px);
  left: 9px;
  top: 1px;
  border-radius: 8px;
  z-index: 1;
  cursor: text;
  display: flex;
}
</style>
